import {createSketchSolverRuntimeSnapshot} from '../constraint/solverHistory/SketchSolverSnapshotFactory';
import type {ISketchObject} from '../../../mesh/sketch/ISketchObject';
import type {SketchPointObject} from '../../../mesh/sketch/SketchPointObject';
import {solveSketchParametricIsland} from './solveSketchParametricIsland';
import type {
    SketchParametricSolveCoordinatorInput,
    SketchParametricSolveCoordinatorOptions,
    SketchParametricSolveCoordinatorResult,
    SketchParametricSolveBaselineCaptureInput,
    SketchParametricRuntimeApplierPort,
    SketchRelationBranchHintRuntimePort,
} from '../../../../types/sketch/solver/SketchParametricSolveCoordinator';
import {SketchParametricRuntimeApplier} from './SketchParametricRuntimeApplier';
import type {CurveOffsetDerivedSyncService} from '../../dimension/derived/CurveOffsetDerivedSyncService';
import {SketchRelationBranchHintRuntime} from './SketchRelationBranchHintRuntime';

/** 空回滚函数。 */
const NOOP_ROLLBACK = (): void => undefined;

/**
 * 草图参数求解运行时协调器。
 *
 * 职责：采集完整 island、建立身份别名、执行一次纯数据求解、原子写回点与实体参数，
 * 并提供同范围 rollback。命令历史、鼠标生命周期和 UI 提示由调用入口负责。
 */
export class SketchParametricSolveCoordinator {
    /** solver island snapshot 工厂。 */
    private readonly createRuntimeSnapshot: NonNullable<
        SketchParametricSolveCoordinatorOptions['createRuntimeSnapshot']
    >;

    /** 纯数据 island 求解端口。 */
    private readonly solveIsland: NonNullable<SketchParametricSolveCoordinatorOptions['solveIsland']>;

    /** 运行时写回端口。 */
    private readonly runtimeApplier: SketchParametricRuntimeApplierPort;

    /** 关系稳定多解分支运行时端口。 */
    private readonly branchHintRuntime: SketchRelationBranchHintRuntimePort;

    /**
     * 创建运行时协调器。
     *
     * @param options 可替换端口；默认使用 cad-core 正式实现。
     */
    public constructor(options: SketchParametricSolveCoordinatorOptions = {}) {
        this.createRuntimeSnapshot = options.createRuntimeSnapshot ?? createSketchSolverRuntimeSnapshot;
        this.solveIsland = options.solveIsland ?? solveSketchParametricIsland;
        this.runtimeApplier = options.runtimeApplier ?? new SketchParametricRuntimeApplier();
        this.branchHintRuntime = options.branchHintRuntime ?? new SketchRelationBranchHintRuntime();
    }

    /**
     * 捕获 FreeCAD 式拖拽会话不可变基线。
     *
     * @param input island 种子与 snapshot 补充端口。
     * @returns pointerdown 时的 solver island；无法采集时返回 null。
     */
    public captureBaseline(
        input: SketchParametricSolveBaselineCaptureInput,
    ): SketchParametricSolveCoordinatorResult['beforeIsland'] {
        return this.createRuntimeSnapshot({
            seedObjects: input.seedObjects,
            supplementProvider: input.supplementProvider,
        }).snapshot;
    }

    /**
     * 注入等距样条派生同步服务（转发给运行时写回器）。
     *
     * @param service 派生同步服务；传 null 清除。
     */
    public configureCurveOffsetDerivedSync(service: CurveOffsetDerivedSyncService | null): void {
        this.runtimeApplier.configureCurveOffsetDerivedSync(service);
    }

    /**
     * 求解并原子写回一次运行时操作。
     *
     * @param input 运行时求解输入。
     * @returns 写回结果及同范围回滚函数。
     */
    public solveAndApply(
        input: SketchParametricSolveCoordinatorInput,
    ): SketchParametricSolveCoordinatorResult {
        const runtimeSnapshot = this.createRuntimeSnapshot({
            seedObjects: input.seedObjects,
            supplementProvider: input.supplementProvider,
        });
        const beforeIsland = runtimeSnapshot.snapshot;

        if (!beforeIsland) {
            /**
             * 空草图没有参数、关系或尺寸方程，无法生成 island 是合法的求解终点。
             *
             * 典型场景是删除草图中最后一个实体：调用方仍需把删除事务标记为成功并提交历史，
             * 但不应伪造空 island 或把“没有方程可解”误报为约束冲突。只有输入仍包含种子对象时，
             * snapshot 缺失才表示运行时图采集异常。
             */
            if (input.seedObjects.length === 0) {
                return {
                    applied: true,
                    beforeIsland: null,
                    solveResult: null,
                    rollback: NOOP_ROLLBACK,
                };
            }
            return {
                applied: false,
                beforeIsland: null,
                solveResult: null,
                rollback: NOOP_ROLLBACK,
                message: '当前运行时对象无法采集 solver island',
            };
        }

        const solveBaseline = input.baselineIsland ?? beforeIsland;
        const currentPointIds = new Set(beforeIsland.pointIds);
        const currentEntityIds = new Set(beforeIsland.entityIds);
        const currentConstraintIds = new Set(beforeIsland.constraintIds);
        const currentDimensionIds = new Set(beforeIsland.dimensionIds);
        const baselineMatchesRuntime = solveBaseline.pointIds.length === beforeIsland.pointIds.length
            && solveBaseline.entityIds.length === beforeIsland.entityIds.length
            && solveBaseline.constraintIds.length === beforeIsland.constraintIds.length
            && solveBaseline.dimensionIds.length === beforeIsland.dimensionIds.length
            && solveBaseline.pointIds.every((pointId) => currentPointIds.has(pointId))
            && solveBaseline.entityIds.every((entityId) => currentEntityIds.has(entityId))
            && solveBaseline.constraintIds.every((constraintId) => currentConstraintIds.has(constraintId))
            && solveBaseline.dimensionIds.every((dimensionId) => currentDimensionIds.has(dimensionId));

        if (!baselineMatchesRuntime) {
            return {
                applied: false,
                beforeIsland,
                solveResult: null,
                rollback: NOOP_ROLLBACK,
                message: '拖拽会话基线与当前草图拓扑不一致，已拒绝过期预览。',
            };
        }

        const runtimePoints = runtimeSnapshot.runtimePoints;
        const runtimeEntities = runtimeSnapshot.runtimeEntities;
        const entityIdAliases = this.createEntityIdAliases(input, runtimePoints, runtimeEntities);
        const islandDimensions = this.filterDimensionsForIsland(
            input.dimensions ?? [],
            solveBaseline,
            entityIdAliases,
        );
        const solveResult = this.solveIsland({
            island: solveBaseline,
            dimensions: islandDimensions,
            entityIdAliases,
            operationIntents: input.operationIntents,
            authoritativePointIds: input.authoritativePointIds,
            authoritativeEntityIds: input.authoritativeEntityIds,
            shouldCancel: () => input.isResultCurrent?.() === false,
        });

        if (!solveResult.applied) {
            return {
                applied: false,
                beforeIsland,
                solveResult,
                rollback: NOOP_ROLLBACK,
                ...(solveResult.message ? {message: solveResult.message} : {}),
            };
        }

        if (input.isResultCurrent?.() === false) {
            return {
                applied: false,
                beforeIsland,
                solveResult,
                rollback: NOOP_ROLLBACK,
                message: '参数解已过期，未写回运行时。',
            };
        }

        const branchHintObjects = [...runtimeEntities, ...runtimePoints];
        const beforeEntityParameters = this.runtimeApplier.captureEntityParameters(runtimeEntities);
        const beforeRelationBranchHints = this.branchHintRuntime.capture(branchHintObjects);
        let rolledBack = false;
        const rollback = (): void => {
            if (rolledBack) {
                return;
            }
            rolledBack = true;
            const rollbackOutcome = this.runtimeApplier.applyPointPositions(
                beforeIsland,
                runtimePoints,
                {deferEdgeRefresh: true},
            );

            this.runtimeApplier.applyEntityParameters(beforeEntityParameters, runtimeEntities);
            this.branchHintRuntime.restore(beforeRelationBranchHints, branchHintObjects);
            try {
                this.runtimeApplier.refreshAffectedEdges(rollbackOutcome.affectedEdges);
            }
            finally {
                // 失败事务排队的异步派生结果不得在 rollback 返回后重新取得写权限。
                this.runtimeApplier.invalidatePendingAsyncGeometry?.(rollbackOutcome.affectedEdges);
            }
            input.requestRender?.();
        };

        try {
            const applyOutcome = this.runtimeApplier.applyPointPositions(
                solveResult.island,
                runtimePoints,
                {deferEdgeRefresh: true},
            );

            if (applyOutcome.missingPointIds.length > 0) {
                rollback();
                return {
                    applied: false,
                    beforeIsland,
                    solveResult,
                    rollback: NOOP_ROLLBACK,
                    message: `参数解写回缺少运行时点：${applyOutcome.missingPointIds.join('、')}`,
                };
            }

            this.runtimeApplier.applyEntityParameters(solveResult.solvedEntityParameters, runtimeEntities);
            const appliedBranchHintCount = this.branchHintRuntime.apply(
                solveResult.resolvedRelationBranchHints,
                branchHintObjects,
            );

            if (appliedBranchHintCount !== solveResult.resolvedRelationBranchHints.size) {
                rollback();
                return {
                    applied: false,
                    beforeIsland,
                    solveResult,
                    rollback: NOOP_ROLLBACK,
                    message: '参数解写回缺少关系领域对象，已恢复本次几何与分支状态。',
                };
            }

            this.runtimeApplier.refreshAffectedEdges(applyOutcome.affectedEdges);
            input.requestRender?.();

            return {
                applied: true,
                beforeIsland,
                solveResult,
                rollback,
            };
        } catch (error) {
            rollback();
            return {
                applied: false,
                beforeIsland,
                solveResult,
                rollback: NOOP_ROLLBACK,
                message: `参数解运行时写回失败：${error instanceof Error ? error.message : String(error)}`,
            };
        }
    }

    /**
     * 构建持久 ID 到运行时 ID 的身份桥接。
     *
     * @param input 运行时求解输入。
     * @param runtimePoints snapshot 同次采集的运行时点。
     * @param runtimeEntities snapshot 同次采集的运行时实体。
     * @returns 身份别名映射。
     */
    private createEntityIdAliases(
        input: SketchParametricSolveCoordinatorInput,
        runtimePoints: readonly SketchPointObject[],
        runtimeEntities: readonly ISketchObject[],
    ): ReadonlyMap<string, string> {
        const aliases = new Map<string, string>();
        const register = (candidate: unknown): void => {
            if (!candidate || typeof candidate !== 'object') {
                return;
            }

            const persistentId = Reflect.get(candidate, 'persistentId');
            const runtimeId = Reflect.get(candidate, 'uuid');

            if (typeof persistentId === 'string' && typeof runtimeId === 'string'
                && persistentId.length > 0 && persistentId !== runtimeId) {
                aliases.set(persistentId, runtimeId);
            }
        };

        input.seedObjects.forEach(register);
        runtimePoints.forEach(register);
        runtimeEntities.forEach(register);
        input.priorityAliases?.forEach((runtimeId, persistentId) => aliases.set(persistentId, runtimeId));

        return aliases;
    }

    /**
     * 把草图级尺寸集合收窄到当前 solver island。
     *
     * Viewer 的尺寸仓库按整张草图保存，而一次关系操作只应求解从直接目标扩展出的
     * 连通岛。若把无关尺寸继续交给 builder，它们会因目标句柄不在当前 island 而被
     * 误判为 unsupported，进而取消本来可解的关系候选。持久 ID 通过别名映射到运行时
     * ID 后参与成员判定，保证保存态尺寸与运行时点/实体使用同一身份空间。
     *
     * @param dimensions 草图内全部尺寸领域快照。
     * @param island 当前运行时 solver island。
     * @param entityIdAliases 持久 ID 到运行时 ID 的身份别名。
     * @returns 目标全部属于当前 island 的尺寸快照。
     */
    private filterDimensionsForIsland(
        dimensions: NonNullable<SketchParametricSolveCoordinatorInput['dimensions']>,
        island: NonNullable<SketchParametricSolveCoordinatorResult['beforeIsland']>,
        entityIdAliases: ReadonlyMap<string, string>,
    ): NonNullable<SketchParametricSolveCoordinatorInput['dimensions']> {
        const memberIds = new Set([...island.entityIds, ...island.pointIds]);

        entityIdAliases.forEach((runtimeId, persistentId) => {
            if (memberIds.has(runtimeId)) {
                memberIds.add(persistentId);
            }
        });

        return dimensions.filter((dimension) => dimension.references.every((reference) => {
            if (!memberIds.has(reference.entityId)) {
                return false;
            }
            return reference.pointId === undefined || memberIds.has(reference.pointId);
        }));
    }
}
